<script setup lang="ts">
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import type {
  TechnicalPassportDimensions,
  TechnicalPassportElement,
  TechnicalPassportYarn,
} from '../types'
import { decorationTypeFromElement } from '../sockDecorations'
import { createSockContourGeometry, sockContourLabelPositions } from './sockContourGeometry'
import SockDecorationLayer from './SockDecorationLayer.vue'

const props = defineProps<{
  dimensions?: TechnicalPassportDimensions
  elements?: TechnicalPassportElement[]
  yarns?: TechnicalPassportYarn[]
  sockType?: string
}>()
const { t } = useI18n()
const previewId = useId().replaceAll(':', '')
const contourClipId = `preview-contour-${previewId}`
const gridId = `preview-grid-${previewId}`
const shadowId = `preview-shadow-${previewId}`

const contour = computed(() => createSockContourGeometry(props.sockType, props.dimensions?.leg_height_mm))
const profile = computed(() => t(`engineering.passportStructure.profileType.${contour.value.profile}`))
const assigned = computed(() =>
  new Set((props.elements ?? []).map((element) => element.position_code).filter(Boolean)),
)
const colorwayYarns = computed(() => (props.yarns ?? [])
  .map((yarn) => ({ ...yarn, previewColor: normalizeColor(yarn.color_code) }))
  .filter((yarn) => yarn.previewColor))

function normalizeColor(value?: string): string | undefined {
  const color = value?.trim()
  return color && /^#[0-9a-f]{6}$/i.test(color) ? color : undefined
}

function patternKind(element?: TechnicalPassportElement): string {
  const pattern = element?.pattern_type?.toLowerCase()
  if (['rib', 'mesh', 'hatch', 'dots', 'waves'].includes(pattern ?? '')) return pattern!
  const knit = element?.knit_type?.toLowerCase()
  if (knit?.startsWith('rib')) return 'rib'
  if (knit === 'mesh') return 'mesh'
  if (knit === 'reinforced') return 'hatch'
  if (knit === 'terry' || knit === 'jacquard') return 'dots'
  if (knit === 'elastic') return 'waves'
  return 'plain'
}

function patternStroke(color?: string): string {
  if (!color) return '#526f8d'
  const red = Number.parseInt(color.slice(1, 3), 16)
  const green = Number.parseInt(color.slice(3, 5), 16)
  const blue = Number.parseInt(color.slice(5, 7), 16)
  return (red * 299 + green * 587 + blue * 114) / 1000 < 120 ? '#ffffff' : '#173f70'
}

function yarnRoleLabel(role?: string): string {
  return role ? t(`engineering.yarnRole.${role}`) : ''
}

const zones = computed(() => {
  const geometry = contour.value
  const positions = sockContourLabelPositions[geometry.labelProfile]
  const yarnByRole = new Map((props.yarns ?? [])
    .filter((yarn) => yarn.role)
    .map((yarn) => [yarn.role!.toLowerCase(), yarn]))
  return [
    { code: 'top_cuff', label: t('engineering.structurePreview.cuff'), path: geometry.paths.cuff },
    { code: 'leg_front', label: t('engineering.passportStructure.zone.leg_front'), path: geometry.paths.legFront, leg: true },
    { code: 'leg_back', label: t('engineering.passportStructure.zone.leg_back'), path: geometry.paths.legBack, leg: true },
    { code: 'ankle_area', label: t('engineering.structurePreview.ankle'), path: geometry.paths.ankle },
    { code: 'heel_area', label: t('engineering.structurePreview.heel'), path: geometry.paths.heel },
    { code: 'instep_area', label: t('engineering.structurePreview.instep'), path: geometry.paths.instep },
    { code: 'sole_area', label: t('engineering.structurePreview.sole'), path: geometry.paths.sole },
    { code: 'toe_area', label: t('engineering.structurePreview.toe'), path: geometry.paths.toe },
  ]
    .filter((zone) => positions[zone.code] && (!zone.leg || geometry.hasLegZones))
    .map((zone) => {
      const element = (props.elements ?? []).find((item) => item.position_code === zone.code && !decorationTypeFromElement(item))
      const yarn = element?.yarn_role ? yarnByRole.get(element.yarn_role.toLowerCase()) : undefined
      const color = normalizeColor(yarn?.color_code)
      return {
        ...zone,
        ...positions[zone.code]!,
        color,
        element,
        yarn,
        pattern: patternKind(element),
        patternId: `zone-pattern-${previewId}-${zone.code.replaceAll('_', '-')}`,
        patternStroke: patternStroke(color),
      }
    })
})
</script>

<template>
  <figure class="drawing-preview">
    <div class="drawing-preview__heading">
      <div><strong>{{ $t('engineering.passportStructure.drawing') }}</strong><span>{{ $t('engineering.passportStructure.profile', { profile }) }}</span></div>
      <span class="drawing-preview__count">{{ $t('engineering.structurePreview.zonesDefined', { count: assigned.size }) }}</span>
    </div>
    <svg :class="`profile-${contour.profile}`" :viewBox="contour.viewBox" role="img" :aria-label="$t('engineering.passportStructure.drawingAria')">
      <defs>
        <clipPath :id="contourClipId"><path :d="contour.paths.base"/></clipPath>
        <pattern :id="gridId" width="16" height="16" patternUnits="userSpaceOnUse">
          <path d="M16 0H0V16" fill="none" stroke="#dbe5ef" stroke-width=".7"/>
        </pattern>
        <pattern
          v-for="zone in zones.filter((item) => item.color)"
          :id="zone.patternId"
          :key="zone.patternId"
          :data-zone="zone.code"
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
        >
          <rect width="8" height="8" :fill="zone.color"/>
          <path v-if="zone.pattern === 'rib'" d="M2 0V8M6 0V8" :stroke="zone.patternStroke" stroke-opacity=".32" stroke-width="1"/>
          <path v-else-if="zone.pattern === 'mesh'" d="M0 0L8 8M8 0L0 8" :stroke="zone.patternStroke" stroke-opacity=".28" stroke-width=".8"/>
          <path v-else-if="zone.pattern === 'hatch'" d="M-2 8L8-2M2 10L10 2" :stroke="zone.patternStroke" stroke-opacity=".3" stroke-width="1"/>
          <g v-else-if="zone.pattern === 'dots'" :fill="zone.patternStroke" fill-opacity=".3"><circle cx="2" cy="2" r=".9"/><circle cx="6" cy="6" r=".9"/></g>
          <path v-else-if="zone.pattern === 'waves'" d="M0 2C2 0 2 4 4 2S6 4 8 2M0 6C2 4 2 8 4 6S6 8 8 6" fill="none" :stroke="zone.patternStroke" stroke-opacity=".32" stroke-width=".8"/>
        </pattern>
        <filter :id="shadowId" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="5" stdDeviation="5" flood-color="#173f70" flood-opacity=".14"/>
        </filter>
      </defs>
      <rect width="380" height="340" :fill="`url(#${gridId})`"/>
      <path class="sock-base" :d="contour.paths.base" :filter="`url(#${shadowId})`"/>
      <g :clip-path="`url(#${contourClipId})`">
        <path
          v-for="zone in zones"
          :key="zone.code"
          class="sock-zone"
          :class="[`zone-${zone.code}`, { assigned: assigned.has(zone.code) }]"
          :d="zone.path"
          :style="zone.color ? { fill: `url(#${zone.patternId})` } : undefined"
        />
      </g>
      <path class="sock-contour-outline" :d="contour.paths.base"/>
      <SockDecorationLayer
        :elements="elements"
        :yarns="yarns"
        :dimensions="dimensions"
        :geometry="contour"
      />
      <g v-for="zone in zones" :key="`${zone.code}-label`" :data-zone="zone.code" :transform="`translate(${zone.x} ${zone.y})`">
        <circle r="8" :class="{ assigned: assigned.has(zone.code) }"/>
        <text x="12" dy="3.5">{{ zone.label }}</text>
      </g>
      <path v-if="contour.hasLegDimension" class="dimension-line" :d="`M${contour.legDimensionX - 5} ${contour.legTop}H${contour.legDimensionX + 4}M${contour.legDimensionX} ${contour.legTop}V${contour.legDimensionBottom}M${contour.legDimensionX - 5} ${contour.legDimensionBottom}H${contour.legDimensionX + 4}`"/>
      <text v-if="contour.hasLegDimension" class="dimension-value" :x="contour.legDimensionX + 9" :y="(contour.legTop + contour.legDimensionBottom) / 2">{{ dimensions?.leg_height_mm ?? '—' }} mm</text>
      <path class="dimension-line" d="M105 326v-9M105 321H335M335 326v-9"/>
      <text class="dimension-value" x="197" y="335">{{ dimensions?.foot_length_mm ?? '—' }} mm</text>
    </svg>
    <div v-if="colorwayYarns.length" class="colorway-legend">
      <strong>{{ $t('engineering.passportStructure.yarnsColors') }}</strong>
      <span v-for="(yarn, index) in colorwayYarns" :key="yarn.id ?? `${yarn.role}-${index}`">
        <i :style="{ backgroundColor: yarn.previewColor }"></i>
        <b>{{ yarn.color_name || yarn.role }}</b>
        <small>{{ yarnRoleLabel(yarn.role) }}<template v-if="yarn.yarn_type"> · {{ yarn.yarn_type }}</template></small>
      </span>
    </div>
    <figcaption>
      <span><i class="legend-dot legend-dot--assigned"></i>{{ $t('engineering.structurePreview.assigned') }}</span>
      <span><i class="legend-dot"></i>{{ $t('engineering.structurePreview.reference') }}</span>
    </figcaption>
  </figure>
</template>

<style scoped>
.drawing-preview{display:grid;gap:14px;margin:0;padding:18px;border:1px solid #c6d5e5;border-radius:8px;background:linear-gradient(145deg,#f5f9fd 0%,#eaf2fa 100%)}
.drawing-preview__heading{display:flex;align-items:center;justify-content:space-between;gap:12px}.drawing-preview__heading>div{display:grid;gap:3px}.drawing-preview__heading strong{color:#173454;font-size:15px}.drawing-preview__heading span,figcaption{color:#66778b;font-size:11px}.drawing-preview__count{padding:5px 9px;border:1px solid #b9d5cc;border-radius:999px;color:#17634d!important;background:#edf8f4;font-weight:700}.drawing-preview svg{display:block;width:min(100%,620px);height:auto;max-height:430px;margin:auto;border:1px solid #cbd8e5;border-radius:6px;background:#fff}.sock-base{fill:#fbfdff;stroke:#123f73;stroke-width:3}.sock-contour-outline{fill:none;stroke:#123f73;stroke-width:3;pointer-events:none}.sock-zone{fill:#e3ecf5;fill-opacity:.9;stroke:#8ca4bd;stroke-width:1}.zone-leg_back,.zone-heel_area,.zone-toe_area{fill:#cbdced}.zone-ankle_area,.zone-sole_area{fill:#d6e3f0}.sock-zone.assigned{fill:#9edfc9;fill-opacity:.94;stroke:#078b66;stroke-width:2}.drawing-preview circle{fill:#7189a6;stroke:#fff;stroke-width:1.5}.drawing-preview circle.assigned{fill:#07996f}.drawing-preview text{fill:#173f70;font-size:9px;font-weight:700}.dimension-line{fill:none;stroke:#627990;stroke-width:1}.dimension-value{font-size:10px!important}.drawing-preview figcaption{display:flex;gap:16px;align-items:center;justify-content:center}.drawing-preview figcaption span{display:flex;gap:6px;align-items:center}.legend-dot{width:9px;height:9px;border:1px solid #7896b9;border-radius:2px;background:#e3ecf5}.legend-dot--assigned{border-color:#078b66;background:#9edfc9}.colorway-legend{display:flex;align-items:center;justify-content:center;gap:7px;flex-wrap:wrap}.colorway-legend>strong{margin-right:3px;color:#52677e;font-size:10px}.colorway-legend>span{display:grid;grid-template-columns:12px auto;grid-template-rows:auto auto;column-gap:6px;padding:5px 8px;border:1px solid #cbd8e5;border-radius:5px;background:#fff}.colorway-legend i{grid-row:1/3;width:12px;height:24px;border:1px solid rgb(23 52 84/22%);border-radius:2px}.colorway-legend b{color:#173454;font-size:10px;line-height:1.2}.colorway-legend small{color:#718096;font-size:8px;line-height:1.2}@media(max-width:600px){.drawing-preview{padding:12px}.drawing-preview__heading{align-items:flex-start;flex-direction:column}.drawing-preview__count{align-self:flex-start}.colorway-legend{justify-content:flex-start}}
</style>
